/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Button, Popover, PopoverContent, PopoverTrigger, ToggleGroup, ToggleGroupItem } from '@quadminds/mindkit';
import { CHAT_MODE } from '@quadminds/mindy-app/enums/chatMode.enum';
import { useByPassContext } from '@quadminds/mindy-app/providers/ByPass/ByPass.provider';
import { MindyStandardRoles } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto, TypologiesEnum } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, FileStack, AlertCircleIcon } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PLATFORM_URL } from '../../../../../config';
import { defaultChatMode, useChatContext } from '../../../../../providers/Chat/Chat.provider';
import { resolveSelectorMode } from '../../../../../utils/chatModePolicy';
import { isKnowledgeUser, requiresApiKey } from '../../../../../utils/utils';

const allowedRoles = new Set([MindyStandardRoles.ADMIN, MindyStandardRoles.GUEST, MindyStandardRoles.MANAGER]);

interface ModeSelectorProps {
    readonly isModeLocked?: () => boolean;
}

export function ModeSelector({ isModeLocked }: ModeSelectorProps) {
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const { isImpersonated } = useByPassContext();
    const currentRoles = useMemo(() => userInfo?.roles || [], [userInfo?.roles]);
    const hasUserTypologies = userInfo
        ? [TypologiesEnum.USER, TypologiesEnum.SUBUSER].includes(userInfo?.typology)
        : true;

    const mindyRoles = useMemo(() => currentRoles.filter((role) => role.startsWith('mindy:')), [currentRoles]);

    let thereIsAProblemWithUserApiKey = false;

    if (requiresApiKey()) {
        if (hasUserTypologies) {
            thereIsAProblemWithUserApiKey = !userInfo?.personalApiKey;
        } else {
            thereIsAProblemWithUserApiKey = !userInfo?.apiKey;
        }
    }

    const { mode, setMode } = useChatContext();
    const { t } = useTranslation();
    const knowledgeUser = isKnowledgeUser(userInfo);
    const effectiveMode = resolveSelectorMode({ userInfo, currentMode: mode });

    const handleToggle = (value: string[]) => {
        const mode = value[value.length - 1] as 'knowledge' | 'logistics' | undefined;
        if (knowledgeUser) {
            setMode(CHAT_MODE.knowledge);
            return;
        }
        if (!mode) return;
        setMode(mode);
    };

    const showButtons = mindyRoles.some((role) => allowedRoles.has(role as MindyStandardRoles)) || isImpersonated;

    useEffect(() => {
        if (knowledgeUser) {
            setMode(CHAT_MODE.knowledge);
        }
    }, [knowledgeUser, setMode]);

    useEffect(() => {
        if (thereIsAProblemWithUserApiKey && mode === defaultChatMode) {
            setMode('knowledge');
        }
    }, [mode, setMode, thereIsAProblemWithUserApiKey]);

    useEffect(() => {
        if (mindyRoles[0] === MindyStandardRoles.CONSULTANT) {
            setMode(CHAT_MODE.knowledge);
        }
    }, [mindyRoles]);

    if (!showButtons) return null;

    return (
        <div className="flex justify-start pr-2 pb-1">
            <ToggleGroup
                type="multiple"
                variant="outline"
                className="space-x-1 p-2"
                value={[effectiveMode]}
                onValueChange={handleToggle}
            >
                {thereIsAProblemWithUserApiKey && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="destructive" size="icon" className="rounded-full">
                                <AlertCircleIcon />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" side="top" className="border-error-100">
                            <div className="flex flex-col gap-2">
                                <span className="text-error-100 text-sm">{t('errors.missingApiKeyMessage')}</span>
                                <Button
                                    onClick={() => {
                                        if (PLATFORM_URL)
                                            globalThis.location.href = PLATFORM_URL + '/configuracion.php';
                                    }}
                                >
                                    {t('errors.missingApiKeyBtn')}
                                </Button>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
                {showButtons && (
                    <>
                        <ToggleGroupItem
                            value="logistics"
                            aria-label="Toggle operation"
                            className={`font-poppins group rounded-full px-2 md:px-4 hover:text-background-foreground hover:bg-gray-100 hover:dark:bg-gray-qm-800 data-[state=on]:!bg-secondary data-[state=on]:hover:bg-secondary data-[state=on]:text-secondary-foreground`}
                            data-testid="chat-mode-logistics"
                            disabled={
                                knowledgeUser ||
                                thereIsAProblemWithUserApiKey ||
                                (isModeLocked?.() && effectiveMode !== 'logistics')
                            }
                        >
                            <Globe className="text-muted-foreground group-data-[state=on]:text-secondary-foreground" />
                            <span className={`hidden group-data-[state=on]:flex max-sm:text-sm md:flex`}>
                                {t('chat.logisticsBtnSelector')}
                            </span>
                        </ToggleGroupItem>
                        <ToggleGroupItem
                            value="knowledge"
                            aria-label="Toggle documents"
                            className={`font-poppins group rounded-full px-2 md:px-4 hover:text-background-foreground hover:bg-gray-100 hover:dark:bg-gray-qm-800 data-[state=on]:!bg-secondary data-[state=on]:hover:bg-secondary data-[state=on]:text-secondary-foreground`}
                            data-testid="chat-mode-knowledge"
                            disabled={isModeLocked?.() && effectiveMode !== 'knowledge'}
                        >
                            <FileStack className="text-muted-foreground group-data-[state=on]:text-secondary-foreground" />
                            <span className={`hidden group-data-[state=on]:flex max-sm:text-sm md:flex`}>
                                {t('chat.knoledgeBtnSelector')}
                            </span>
                        </ToggleGroupItem>
                    </>
                )}
            </ToggleGroup>
        </div>
    );
}

export default ModeSelector;
